import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { ApiService, BulkQuizStats } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';

export type BulkQuizMode = 'missing' | 'all';

@Component({
  selector: 'app-bulk-quiz-generation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule],
  templateUrl: './bulk-quiz-generation-modal.component.html',
  styleUrl: './bulk-quiz-generation-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkQuizGenerationModalComponent implements OnChanges, OnDestroy {
  @Input() courseId!: string;
  @Input() isVisible = false;

  @Output() closed = new EventEmitter<void>();
  @Output() generationStarted = new EventEmitter<void>();

  private api = inject(ApiService);
  private toast = inject(ToastService);
  private chRef = inject(ChangeDetectorRef);

  selectedMode: BulkQuizMode = 'missing';
  isStarting = false;
  isLoadingStats = false;
  stats: BulkQuizStats | null = null;

  get classCountForMode(): number {
    if (!this.stats) return 0;
    return this.selectedMode === 'missing' ? this.stats.missingClasses : this.stats.totalClasses;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible && this.courseId) {
      this.loadStats();
    }
  }

  ngOnDestroy(): void {}

  private async loadStats(): Promise<void> {
    this.isLoadingStats = true;
    this.stats = null;
    this.chRef.markForCheck();
    try {
      this.stats = await firstValueFrom(this.api.getBulkQuizStats(this.courseId));
    } catch {
      // Stats are optional — modal still works without them
    } finally {
      this.isLoadingStats = false;
      this.chRef.markForCheck();
    }
  }

  async startGeneration(): Promise<void> {
    this.isStarting = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.generateBulkClassQuizAdmin(this.courseId, this.selectedMode));
      this.generationStarted.emit();
      this.close();
    } catch (err: any) {
      const message = err?.error?.message ?? 'Error al iniciar la generación. Inténtalo de nuevo.';
      this.toast.presentErrorToast(message);
    } finally {
      this.isStarting = false;
      this.chRef.markForCheck();
    }
  }

  close(): void {
    this.selectedMode = 'missing';
    this.stats = null;
    this.closed.emit();
  }
}
